import { isAxiosError } from "./client";
const isAccountConflictPayload = (value) => {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }
    const payload = value;
    return ((payload.provider === "google" || payload.provider === "apple") &&
        typeof payload.email === "string" &&
        payload.email.length > 0 &&
        typeof payload.linkTicket === "string" &&
        payload.linkTicket.length > 0 &&
        typeof payload.expiresAt === "string" &&
        payload.expiresAt.length > 0);
};
/** Extracts the additive guided-link payload while preserving payload-less 409s. */
export function getAccountConflictPayload(error) {
    const details = isApiClientError(error)
        ? error.code === "AccountConflict"
            ? error.details
            : null
        : isAxiosError(error) && extractBackendErrorCode(error) === "AccountConflict"
            ? error.response?.data
            : null;
    if (details === null || typeof details !== "object" || Array.isArray(details)) {
        return null;
    }
    const payload = details.payload;
    return isAccountConflictPayload(payload) ? payload : null;
}
/**
 * Structured API client error that preserves HTTP status, backend error code,
 * and raw response data instead of flattening everything to a plain Error.
 */
export class ApiClientError extends Error {
    constructor(input) {
        super(input.message);
        this.isApiClientError = true;
        this.name = "ApiClientError";
        this.code = input.code;
        this.status = input.status;
        this.details = input.details;
        this.originalError = input.originalError;
    }
}
export function isApiClientError(error) {
    if (error instanceof ApiClientError) {
        return true;
    }
    if (error == null || typeof error !== "object") {
        return false;
    }
    const candidate = error;
    return (candidate.isApiClientError === true &&
        typeof candidate.code === "string" &&
        typeof candidate.message === "string");
}
function isPtOverlapConflict(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        return false;
    const conflict = value;
    return (conflict.type === "class_occurrence" || conflict.type === "blocked_exception") && typeof conflict.startAt === "string" && typeof conflict.endAt === "string";
}
function isPtOverlapWarningError(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        return false;
    const warning = value;
    return warning.code === "OverlapWarning" && typeof warning.message === "string" && warning.confirmable === true && Array.isArray(warning.conflicts) && warning.conflicts.every(isPtOverlapConflict);
}
export function getPtOverlapWarning(error) {
    const details = isApiClientError(error)
        ? error.code === "OverlapWarning" ? error.details : null
        : isAxiosError(error) && extractBackendErrorCode(error) === "OverlapWarning" ? error.response?.data : null;
    if (details === null || typeof details !== "object" || Array.isArray(details))
        return null;
    const warning = details.error;
    return isPtOverlapWarningError(warning) ? warning : null;
}
function isClassOverlapWarningError(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        return false;
    const warning = value;
    const appointments = warning.warnings?.affectedAppointments;
    return warning.code === "OverlapWarning"
        && typeof warning.message === "string"
        && warning.confirmable === true
        && Array.isArray(appointments)
        && appointments.every((appointment) => appointment != null
            && typeof appointment.id === "number"
            && typeof appointment.startAt === "string"
            && typeof appointment.endAt === "string"
            && typeof appointment.memberName === "string");
}
export function getClassOverlapWarning(error) {
    const details = isApiClientError(error)
        ? error.code === "OverlapWarning" ? error.details : null
        : isAxiosError(error) && extractBackendErrorCode(error) === "OverlapWarning" ? error.response?.data : null;
    if (details === null || typeof details !== "object" || Array.isArray(details))
        return null;
    const warning = details.error;
    return isClassOverlapWarningError(warning) ? warning : null;
}
function extractBackendErrorCode(error) {
    const data = error.response?.data;
    if (data != null && typeof data === "object") {
        if ("code" in data && typeof data.code === "string") {
            return data.code;
        }
        const nested = data.error;
        if (nested != null &&
            typeof nested === "object" &&
            "code" in nested &&
            typeof nested.code === "string") {
            return nested.code;
        }
    }
    return undefined;
}
function extractBackendMessage(error) {
    const data = error.response?.data;
    if (data != null && typeof data === "object") {
        if ("message" in data && typeof data.message === "string") {
            return data.message;
        }
        const nested = data.error;
        if (nested != null &&
            typeof nested === "object" &&
            "message" in nested &&
            typeof nested.message === "string") {
            return nested.message;
        }
    }
    return undefined;
}
/**
 * Convert an unknown error (usually an AxiosError) into an ApiClientError,
 * preserving whatever structured metadata the backend returned.
 */
export function createApiClientErrorFromAxios(error) {
    if (!isAxiosError(error)) {
        return new ApiClientError({
            code: "UNKNOWN_ERROR",
            message: error instanceof Error ? error.message : "Unknown error",
            originalError: error,
        });
    }
    const status = error.response?.status;
    const responseData = error.response?.data;
    const code = extractBackendErrorCode(error) ?? error.code ?? "HTTP_ERROR";
    const message = extractBackendMessage(error) ?? error.message ?? "Request failed";
    return new ApiClientError({
        code,
        message,
        status,
        details: responseData,
        originalError: error,
    });
}
export function createNoDataReceivedError() {
    return new ApiClientError({
        code: "NO_DATA_RECEIVED",
        message: "No data received from the server",
    });
}
export function createMalformedApiResponseError(raw) {
    return new ApiClientError({
        code: "MALFORMED_API_RESPONSE",
        message: "Malformed API response",
        details: raw,
    });
}
export function createApiClientErrorFromApiResponse(response) {
    const code = response.error?.code ?? "API_RESPONSE_ERROR";
    const message = response.error?.message ?? response.message ?? "Request failed";
    return new ApiClientError({
        code,
        message,
        details: response,
    });
}
